import { defineStore } from 'pinia';
import type { User } from '@/data/User';
import { httpPut } from '@/api/http';
import { V2_USERS_STATUS } from '@/constants/endpoint';
import { computed, watch } from 'vue';
import type { StatusType } from '@/data/UserAvailability';
import { useQueryClient } from '@tanstack/vue-query';
import { useContacts } from '@/api/useContacts';
import { useLoggedInUser } from '@/api/useLoggedInUser';
import type { ContactExtended } from '@/data/contacts/Contact';
import { USERS_AVAILABILITY_QUERY_KEY } from '@/api/useUsersAvailability';
import type { AgentDTO } from '@/data/Agent';

export interface LoggedUser extends User {
  domain: string;
  contact?: ContactExtended;
  agent?: AgentDTO;
}

export const useUserStore = defineStore('user', () => {
  const queryClient = useQueryClient();
  const { data: loggedUser } = useLoggedInUser();
  const { data: contacts } = useContacts();

  const _contact = computed(() => {
    return contacts.value?.find((contact) => contact.uuid === loggedUser.value?.contactUuid);
  });

  const _loggedInUser = computed(() => {
    return {
      ...loggedUser.value,
      contact: _contact.value,
      userStatus: _contact.value?.user?.userStatus,
    } as LoggedUser;
  });

  const setUserStatusType = async (updatedStatus: StatusType) => {
    httpPut(`${V2_USERS_STATUS}/${updatedStatus}`).then((_) => {
      queryClient.invalidateQueries({ queryKey: [USERS_AVAILABILITY_QUERY_KEY] });
    });
  };

  // It's a rare corner case that may be resolved differently in the future - https://jira.wtg-innovation.com/browse/WCP-961
  watch(
    () => loggedUser.value?.extension?.extension,
    (newExtension, oldExtension) => {
      if (oldExtension && oldExtension !== newExtension) {
        location.reload();
      }
    }
  );

  return {
    _loggedInUser,
    setUserStatusType,
  };
});
